import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DashboardLayout } from "../../components/DashboardLayout";
import { links } from "./SuperAdminPages";
import { ActionPromptModal } from "../../components/ActionPromptModal";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Search, Plus, Filter, RefreshCw, Eye, Pencil,
  Pause, Ban, RefreshCcw, Trash2, ArrowUpDown, X,
  Download, Building2, AlertTriangle,
} from "lucide-react";
import ChevronDown from "lucide-react/dist/esm/icons/chevron-down";
import MoreVertical from "lucide-react/dist/esm/icons/ellipsis-vertical";
import { ClinicStatusBadge, RenewalBadge, PaymentStatusBadge } from "./components/ClinicBadges";

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "trial", label: "Trial" },
  { value: "suspended", label: "Suspended" },
  { value: "disabled", label: "Disabled" },
  { value: "cancelled", label: "Cancelled" },
  { value: "deleted", label: "Deleted" },
];
const TRIAL_OPTIONS = [
  { value: "", label: "Any trial state" },
  { value: "active", label: "Trial active" },
  { value: "ending_soon", label: "Trial ending ≤ 7d" },
  { value: "expired", label: "Trial expired" },
  { value: "none", label: "Not on trial" },
];
const RENEWAL_OPTIONS = [
  { value: "", label: "Any renewal" },
  { value: "7", label: "Due ≤ 7 days" },
  { value: "15", label: "Due ≤ 15 days" },
  { value: "30", label: "Due ≤ 30 days" },
  { value: "overdue", label: "Overdue" },
];
const PAYMENT_OPTIONS = [
  { value: "", label: "Any payment" },
  { value: "paid", label: "Paid" },
  { value: "pending", label: "Pending" },
  { value: "overdue", label: "Overdue" },
  { value: "trial", label: "Trial" },
];

export default function SuperAdminClinicsListPage() {
  const navigate = useNavigate();
  const [clinics, setClinics] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [trialFilter, setTrialFilter] = useState("");
  const [renewalFilter, setRenewalFilter] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState(-1);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [impersonateModal, setImpersonateModal] = useState(null);
  const [impersonateReason, setImpersonateReason] = useState("Support request");
  const [bulkReasonModal, setBulkReasonModal] = useState(null);

  const fetchClinics = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.append("q", q);
      if (statusFilter) params.append("status_filter", statusFilter);
      if (planFilter) params.append("plan_id", planFilter);
      if (trialFilter) params.append("trial_status", trialFilter);
      if (renewalFilter) params.append("renewal_due", renewalFilter);
      if (paymentFilter) params.append("payment_status", paymentFilter);
      if (includeDeleted) params.append("include_deleted", "true");
      params.append("sort_by", sortBy);
      params.append("sort_dir", String(sortDir));
      const r = await api.get(`/admin/clinics?${params.toString()}`);
      setClinics(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load clinics");
    }
    setLoading(false);
  }, [q, statusFilter, planFilter, trialFilter, renewalFilter, paymentFilter, includeDeleted, sortBy, sortDir]);

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { fetchClinics(); }, [fetchClinics]);

  const onSearch = (e) => { e.preventDefault(); fetchClinics(); };

  const clearFilters = () => {
    setQ(""); setStatusFilter(""); setPlanFilter(""); setTrialFilter("");
    setRenewalFilter(""); setPaymentFilter(""); setIncludeDeleted(false);
  };

  const toggleSelect = (cid) => {
    const next = new Set(selected);
    if (next.has(cid)) next.delete(cid); else next.add(cid);
    setSelected(next);
  };
  const toggleSelectAll = () => {
    if (selected.size === clinics.length) setSelected(new Set());
    else setSelected(new Set(clinics.map((c) => c.clinic_id)));
  };

  const toggleSort = (field) => {
    if (sortBy === field) setSortDir(sortDir === 1 ? -1 : 1);
    else { setSortBy(field); setSortDir(-1); }
  };

  const runBulk = async (action) => {
    if (!selected.size) return toast.error("No clinics selected");
    setBulkReasonModal({ action, ids: Array.from(selected) });
  };

  const submitBulk = async (reason) => {
    if (!bulkReasonModal) return;
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      toast.error("Reason is required");
      return;
    }
    setBulkBusy(true);
    try {
      const r = await api.post("/admin/clinics/bulk", {
        clinic_ids: bulkReasonModal.ids,
        action: bulkReasonModal.action,
        reason: trimmedReason,
      });
      const ok = r.data?.ok?.length || 0;
      const failed = r.data?.failed?.length || 0;
      if (failed === 0) toast.success(`${ok} clinics updated`);
      else toast.error(`${ok} ok, ${failed} failed`);
      setSelected(new Set());
      setBulkReasonModal(null);
      fetchClinics();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk action failed");
    }
    setBulkBusy(false);
  };

  const exportCsv = () => {
    const rows = clinics.map((c) => ({
      clinic_id: c.clinic_id, name: c.name, slug: c.slug,
      admin_name: c.admin?.name || "", admin_email: c.admin?.email || "",
      plan: c.plan?.name || "", subscription_status: c.subscription_status,
      is_trial: c.is_trial, trial_remaining_days: c.trial_remaining_days,
      next_renewal_date: c.next_renewal_date || "",
      payment_status: c.payment_status || "",
      users_count: c.users_count, doctors_count: c.doctors_count,
      patients_count: c.patients_count, appointments_count: c.appointments_count,
      revenue: c.revenue, revenue_this_month: c.revenue_this_month,
      created_at: c.created_at,
    }));
    if (!rows.length) return toast.error("No data to export");
    const headers = Object.keys(rows[0]);
    const escape = (v) => {
      const s = v === null || v === undefined ? "" : String(v);
      // CSV injection guard
      const sanitized = /^[=+\-@]/.test(s) ? `'${s}` : s;
      return /[",\n]/.test(sanitized) ? `"${sanitized.replace(/"/g, '""')}"` : sanitized;
    };
    const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => escape(r[h])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `medigenie-clinics-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const impersonate = async (c) => {
    if (!c.admin) return toast.error("Clinic has no admin to impersonate");
    try {
      await api.post("/admin/impersonate/start", {
        impersonated_user_id: c.admin.user_id,
        reason: impersonateReason,
      });
      toast.success("Impersonation started");
      window.location.href = "/clinic-admin";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const activeFilterCount = useMemo(() => [
    statusFilter, planFilter, trialFilter, renewalFilter, paymentFilter, includeDeleted ? "x" : "",
  ].filter(Boolean).length, [statusFilter, planFilter, trialFilter, renewalFilter, paymentFilter, includeDeleted]);

  return (
    <DashboardLayout links={links} title="Clinics">
      {/* Toolbar */}
      <div className="mg-card mb-4">
        <form onSubmit={onSearch} className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className="mg-input pl-9 w-full"
              placeholder="Search clinic name, slug, phone, email…"
              value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="clinics-search-input"
            />
          </div>
          <button type="submit" className="mg-btn-secondary text-sm" data-testid="clinics-search-btn">
            <Search className="w-4 h-4" /> Search
          </button>
          <select className="mg-input text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="filter-status">
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select className="mg-input text-sm" value={planFilter} onChange={(e) => setPlanFilter(e.target.value)} data-testid="filter-plan">
            <option value="">All plans</option>
            {plans.map((p) => <option key={p.plan_id} value={p.plan_id}>{p.name}</option>)}
          </select>
          <select className="mg-input text-sm" value={trialFilter} onChange={(e) => setTrialFilter(e.target.value)} data-testid="filter-trial">
            {TRIAL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select className="mg-input text-sm" value={renewalFilter} onChange={(e) => setRenewalFilter(e.target.value)} data-testid="filter-renewal">
            {RENEWAL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select className="mg-input text-sm" value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)} data-testid="filter-payment">
            {PAYMENT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={includeDeleted} onChange={(e) => setIncludeDeleted(e.target.checked)} data-testid="filter-include-deleted" />
            Include deleted
          </label>
          {activeFilterCount > 0 && (
            <button type="button" onClick={clearFilters} className="text-xs text-mg-medical font-semibold inline-flex items-center gap-1" data-testid="clear-filters">
              <X className="w-3.5 h-3.5" /> Clear ({activeFilterCount})
            </button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <button type="button" onClick={exportCsv} className="mg-btn-secondary text-sm" data-testid="export-csv-btn">
              <Download className="w-4 h-4" /> CSV
            </button>
            <button type="button" onClick={fetchClinics} className="mg-btn-secondary text-sm" data-testid="refresh-clinics">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <Link to="/super-admin/clinics/new" className="mg-btn-primary text-sm" data-testid="create-clinic-btn">
              <Plus className="w-4 h-4" /> New clinic
            </Link>
          </div>
        </form>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="mg-card mb-4 bg-mg-medical/5 border-mg-medical/20" data-testid="bulk-action-bar">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="text-sm font-semibold text-mg-navy">{selected.size} selected</div>
            <div className="flex flex-wrap gap-2">
              <button disabled={bulkBusy} onClick={() => runBulk("suspend")} className="mg-btn-secondary text-xs" data-testid="bulk-suspend">
                <Pause className="w-3.5 h-3.5" /> Suspend
              </button>
              <button disabled={bulkBusy} onClick={() => runBulk("disable")} className="mg-btn-secondary text-xs" data-testid="bulk-disable">
                <Ban className="w-3.5 h-3.5" /> Disable
              </button>
              <button disabled={bulkBusy} onClick={() => runBulk("reactivate")} className="mg-btn-secondary text-xs" data-testid="bulk-reactivate">
                <RefreshCcw className="w-3.5 h-3.5" /> Reactivate
              </button>
              <button disabled={bulkBusy} onClick={() => runBulk("restore")} className="mg-btn-secondary text-xs" data-testid="bulk-restore">
                <RefreshCcw className="w-3.5 h-3.5" /> Restore
              </button>
              <button disabled={bulkBusy} onClick={() => runBulk("soft_delete")} className="text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-700 bg-red-50 hover:bg-red-100 inline-flex items-center gap-1.5" data-testid="bulk-soft-delete">
                <Trash2 className="w-3.5 h-3.5" /> Soft delete
              </button>
              <button onClick={() => setSelected(new Set())} className="text-xs text-slate-600">Cancel</button>
            </div>
          </div>
        </div>
      )}

      <ActionPromptModal
        open={Boolean(bulkReasonModal)}
        title={bulkReasonModal ? `Reason for bulk ${bulkReasonModal.action} (${bulkReasonModal.ids.length} clinics)` : "Bulk action reason"}
        label="Reason"
        placeholder="Bulk admin action"
        initialValue="Bulk admin action"
        confirmLabel="Apply"
        busy={bulkBusy}
        onCancel={() => setBulkReasonModal(null)}
        onConfirm={submitBulk}
      />

      {/* Table */}
      <div className="mg-card overflow-x-auto p-0">
        <table className="w-full text-sm" data-testid="clinics-table">
          <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
            <tr>
              <th className="px-3 py-3 w-8">
                <input type="checkbox" checked={selected.size > 0 && selected.size === clinics.length}
                  onChange={toggleSelectAll} data-testid="select-all-clinics" />
              </th>
              <th className="px-2 py-3 cursor-pointer" onClick={() => toggleSort("name")}>
                <span className="inline-flex items-center gap-1">Clinic <ArrowUpDown className="w-3 h-3" /></span>
              </th>
              <th className="px-2 py-3">Admin</th>
              <th className="px-2 py-3">Plan</th>
              <th className="px-2 py-3">Status</th>
              <th className="px-2 py-3">Renewal</th>
              <th className="px-2 py-3 text-center">Users</th>
              <th className="px-2 py-3 text-center">Doctors</th>
              <th className="px-2 py-3 text-center">Patients</th>
              <th className="px-2 py-3 text-center">Appts/mo</th>
              <th className="px-2 py-3 text-right">Revenue (mo)</th>
              <th className="px-3 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="12" className="px-3 py-8 text-center text-slate-400">Loading clinics…</td></tr>
            )}
            {!loading && clinics.length === 0 && (
              <tr><td colSpan="12" className="px-3 py-8 text-center text-slate-400">No clinics match your filters.</td></tr>
            )}
            {!loading && clinics.map((c) => (
              <tr key={c.clinic_id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`clinic-row-${c.slug}`}>
                <td className="px-3 py-3">
                  <input type="checkbox" checked={selected.has(c.clinic_id)}
                    onChange={() => toggleSelect(c.clinic_id)}
                    data-testid={`select-clinic-${c.slug}`} />
                </td>
                <td className="px-2 py-3">
                  <Link to={`/super-admin/clinics/${c.clinic_id}`} className="font-bold text-mg-navy hover:text-mg-medical" data-testid={`clinic-name-${c.slug}`}>
                    {c.name}
                  </Link>
                  <div className="text-[10px] text-slate-500 font-mono">/{c.slug}</div>
                </td>
                <td className="px-2 py-3">
                  <div className="text-xs font-semibold text-mg-navy">{c.admin?.name || "—"}</div>
                  <div className="text-[10px] text-slate-500">{c.admin?.email || ""}</div>
                </td>
                <td className="px-2 py-3 text-xs">
                  {c.plan ? (
                    <>
                      <div className="font-semibold text-mg-navy">{c.plan.name}</div>
                      <div className="text-[10px] text-slate-500">₹{(c.plan.monthly_price || 0).toLocaleString()}/{(c.plan.billing_cycle || "mo").slice(0, 2)}</div>
                    </>
                  ) : "—"}
                </td>
                <td className="px-2 py-3">
                  <ClinicStatusBadge status={c.subscription_status || c.status} trial={c.is_trial} trialDays={c.trial_remaining_days} />
                </td>
                <td className="px-2 py-3 space-y-1">
                  <RenewalBadge days={c.renewal_days_remaining} isTrial={c.is_trial && (c.trial_remaining_days !== null)} />
                  <PaymentStatusBadge status={c.payment_status} />
                </td>
                <td className="px-2 py-3 text-center text-xs font-semibold">{c.users_count}</td>
                <td className="px-2 py-3 text-center text-xs font-semibold">{c.doctors_count}</td>
                <td className="px-2 py-3 text-center text-xs font-semibold">{c.patients_count}</td>
                <td className="px-2 py-3 text-center text-xs font-semibold">{c.appointments_this_month}</td>
                <td className="px-2 py-3 text-right text-xs font-bold text-mg-medical">₹{(c.revenue_this_month || 0).toLocaleString()}</td>
                <td className="px-3 py-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    <Link to={`/super-admin/clinics/${c.clinic_id}`} className="p-1.5 hover:bg-slate-100 rounded" title="View" data-testid={`view-${c.slug}`}>
                      <Eye className="w-4 h-4 text-mg-medical" />
                    </Link>
                    <Link to={`/super-admin/clinics/${c.clinic_id}/edit`} className="p-1.5 hover:bg-slate-100 rounded" title="Edit" data-testid={`edit-${c.slug}`}>
                      <Pencil className="w-4 h-4 text-slate-600" />
                    </Link>
                    <Link to={`/super-admin/clinics/${c.clinic_id}/subscription`} className="p-1.5 hover:bg-slate-100 rounded text-[10px] font-bold text-mg-medical" title="Subscription" data-testid={`sub-${c.slug}`}>
                      Sub
                    </Link>
                    <button onClick={() => setImpersonateModal(c)} className="p-1.5 hover:bg-slate-100 rounded" title="Impersonate" data-testid={`impersonate-${c.slug}`}>
                      <Building2 className="w-4 h-4 text-mg-gold" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {impersonateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="font-bold text-mg-navy text-lg flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-mg-gold" /> Impersonate clinic admin</h3>
            <p className="text-sm text-slate-600 mt-2">You are about to impersonate <strong>{impersonateModal.admin?.name}</strong> of <strong>{impersonateModal.name}</strong>. All actions are audited.</p>
            <label className="text-sm font-medium text-slate-700 mt-4 block">Reason</label>
            <select value={impersonateReason} onChange={(e) => setImpersonateReason(e.target.value)} className="mg-input mt-1 w-full" data-testid="impersonation-reason">
              <option>Support request</option>
              <option>Onboarding help</option>
              <option>Troubleshooting issue</option>
              <option>Training</option>
              <option>Billing assistance</option>
            </select>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setImpersonateModal(null)} className="mg-btn-secondary flex-1 justify-center">Cancel</button>
              <button onClick={() => impersonate(impersonateModal)} className="mg-btn-primary flex-1 justify-center" data-testid="confirm-impersonate">Start impersonation</button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
